/**
 * 施術録検索 result table (body only — parent renders 該当件数).
 * Columns: checkbox (page select-all), 受診者番号, 受診者名, 施術年月.
 * Pagination UI is intentionally not rendered — this screen does not require it
 * per spec. Backend still returns pagination meta; just ignored on the client.
 */

import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { AppCheckbox, AppTable, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import type { ColumnsType } from 'antd/es/table';
import { convGtJMonth } from '@/utils/dateConverter';
import type { TreatmentRecordListItem } from '../types';

interface Props {
  data: TreatmentRecordListItem[];
  selectedIds: Set<number>;
  onSelectChange: (ids: Set<number>) => void;
  isLoading: boolean;
}

/** Format YYYYMM integer → Japanese era month (e.g. 令和06年04月) */
function formatTreatmentYearMonth(ym: number | null | undefined): string {
  if (!ym) return '';
  const s = String(ym);
  if (s.length !== 6) return s;
  const iso = `${s.substring(0, 4)}-${s.substring(4, 6)}-01`;
  const d = dayjs(iso);
  return d.isValid() ? convGtJMonth(d) : s;
}

const TreatmentRecordTable: React.FC<Props> = ({
  data,
  selectedIds,
  onSelectChange,
  isLoading,
}) => {
  const pageIds = useMemo(() => data.map(r => r.id), [data]);
  const allPageChecked = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));

  const handleTogglePageAll = useCallback(
    (checked: boolean) => {
      const next = new Set(selectedIds);
      if (checked) pageIds.forEach(id => next.add(id));
      else pageIds.forEach(id => next.delete(id));
      onSelectChange(next);
    },
    [pageIds, selectedIds, onSelectChange]
  );

  const handleToggleRow = useCallback(
    (id: number, checked: boolean) => {
      const next = new Set(selectedIds);
      if (checked) next.add(id);
      else next.delete(id);
      onSelectChange(next);
    },
    [selectedIds, onSelectChange]
  );

  const columns: ColumnsType<TreatmentRecordListItem> = [
    {
      title: (
        <AppCheckbox
          checked={allPageChecked}
          onChange={e => handleTogglePageAll(e.target.checked)}
        />
      ),
      key: 'checkbox',
      width: 56,
      align: 'center',
      render: (_, record) => (
        <AppCheckbox
          checked={selectedIds.has(record.id)}
          onChange={e => handleToggleRow(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 160,
      render: (value: string, record) => (
        <PatientEditLink
          patientId={record.patient_id}
          value={value}
          className="inline-flex items-center gap-2"
          ariaLabel="受診者情報"
        />
      ),
    },
    {
      title: '受診者名',
      dataIndex: 'patient_name',
      key: 'patient_name',
      render: (value: string) => (
        <AppTooltip title={value} showOnlyOnOverflow>
          <div className="break-all line-clamp-2">{value}</div>
        </AppTooltip>
      ),
    },
    {
      title: '施術年月',
      dataIndex: 'treatement_yearmonth',
      key: 'treatement_yearmonth',
      width: 180,
      render: (value: number) => formatTreatmentYearMonth(value),
    },
  ];

  return (
    <AppTable<TreatmentRecordListItem>
      rowKey={record => `${record.id}-${record.record_print_id ?? 'no-print-adjustment'}`}
      columns={columns}
      dataSource={data}
      loading={isLoading}
      pagination={false}
      tableLayout="auto"
    />
  );
};

export default TreatmentRecordTable;
